import { useMemo, useState } from 'react'
import type { EdgeConfigResult, EdgeDevice, EdgeMachine, EdgeTag } from '../../types'
import { ItemListModal, ItemSection } from './MachineItems'
import { CycleDialog, OperationDialog } from './MasterGroupDialogs'
import type { SpecTagOf } from './specLookup'
import { ITEM_SECTIONS, currentReading } from './shared'
import type { ItemListKey, TagReading, TagResolved } from './shared'
import { Empty, Field, Section, TagRef } from './settingsUi'
import { ReadingTimeCell, ReadingValueCell } from './ui'

// 엣지 MachineOperationDetectType / MachineCycleMasterType 의 Description
const OPERATION_TYPE_LABELS: Record<string, string> = { NONE: '사용 안 함', CounterDetective: '카운터 신호 감지' }
const CYCLE_TYPE_LABELS: Record<string, string> = {
  None: '사용 안 함', SingleChangeDetective: '단일 신호 감지', StartEndChangeDetective: '시작/종료 신호 감지',
}

interface Props {
  serverId: string
  machine: EdgeMachine
  devices: EdgeDevice[] | undefined
  specTagOf: SpecTagOf | null // 이 서버에서 고른 명세서 — 항목 코드 기본값 {설비코드}_{Alias} 의 명세 쪽 Alias (Edge 태그 Alias 가 비었을 때)
  live: Record<string, TagReading>
  onConfigSaved: (result: EdgeConfigResult) => void
  lockReason: string | null // 있으면 수정 버튼을 막는다 (모니터링 중·오프라인)
  onEditMachine: () => void
  onDeleteMachine: () => void
}

// 마스터데이터 [설정]: 설비 신호가 어느 태그에 어떤 조건으로 묶였는지 + 그 태그의 지금 값.
// Alarm·Mete·Parameter·Signal 은 팝업에서 추가·수정·삭제하고, Operation·Cycle 은 다이얼로그에서 설정한다.
// 저장 후 처리(목록 다시 받기·재시작 안내)는 MasterDataTab 이 라인·설비 변경과 함께 맡는다
export default function MachineSettings({ serverId, machine, devices, specTagOf, live, onConfigSaved, lockReason, onEditMachine, onDeleteMachine }: Props) {
  const [opened, setOpened] = useState<{ key: ItemListKey; mode: 'view' | 'add' } | null>(null)
  const [conditionDialog, setConditionDialog] = useState<'operation' | 'cycle' | null>(null)

  // Operation·Cycle 은 태그 코드로, 목록 항목은 태그 Id 로 연결된다 (엣지 저장 형식 그대로)
  const { byCode, byId } = useMemo(() => {
    if (!devices) return { byCode: null, byId: null }
    const tags = devices.flatMap(d => d.tags)
    return {
      byCode: new Map<string, EdgeTag>(tags.map(t => [t.code, t])),
      byId: new Map<string, EdgeTag>(tags.map(t => [t.id, t])),
    }
  }, [devices])

  const tagByCode = (code: string | null): TagResolved => (byCode ? (code ? byCode.get(code) ?? null : null) : undefined)
  const tagById = (id: string): TagResolved => (byId ? byId.get(id) ?? null : undefined)
  const readingOf = (tag: TagResolved) => (tag ? currentReading(tag, live) : null)

  const { operation: op, cycle } = machine
  const openedSection = opened && ITEM_SECTIONS.find(x => x.key === opened.key)

  return (
    <div className="space-y-4">
      <Section title="기본 정보" action={
        <span className="flex items-center gap-3">
          <button onClick={onEditMachine} disabled={!!lockReason} title={lockReason ?? undefined}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed">수정</button>
          <button onClick={onDeleteMachine} disabled={!!lockReason} title={lockReason ?? undefined}
            className="text-xs text-red-500 hover:text-red-600 disabled:text-gray-300 disabled:cursor-not-allowed">삭제</button>
        </span>
      }>
        <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 px-4 py-3 text-sm">
          <Field label="설비코드"><span className="font-mono">{machine.code}</span></Field>
          <Field label="설비명">{machine.name}</Field>
          <Field label="설명">{machine.description || '-'}</Field>
        </dl>
      </Section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Section title="Operation" note={OPERATION_TYPE_LABELS[op?.type ?? 'NONE'] ?? op?.type}
          action={<SetButton lockReason={lockReason} onClick={() => setConditionDialog('operation')} />}>
          {!op || op.type === 'NONE' ? (
            <Empty>가동 감지를 사용하지 않습니다.</Empty>
          ) : (
            <ConditionTable rows={[
              { label: '감지 태그', tagCode: op.tagCode },
            ]} tagOf={tagByCode} readingOf={readingOf}>
              <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 px-4 py-3 text-sm border-t border-gray-50">
                <Field label="가동 값">{op.runStateValue ?? '-'}</Field>
                <Field label="정지 값">{op.stopStateValue ?? '-'}</Field>
                <Field label="무시 값">{op.ignoreTagValue ?? '-'}</Field>
                <Field label="기준 시간">{op.criteria.toLocaleString()}ms</Field>
                <Field label="MES">{op.mesRegisterable ? `등록 · ${op.code ?? '-'}` : '-'}</Field>
              </dl>
            </ConditionTable>
          )}
        </Section>

        <Section title="Cycle" note={CYCLE_TYPE_LABELS[cycle?.cycleType ?? 'None'] ?? cycle?.cycleType}
          action={<SetButton lockReason={lockReason} onClick={() => setConditionDialog('cycle')} />}>
          {!cycle || cycle.cycleType === 'None' ? (
            <Empty>사이클 감지를 사용하지 않습니다.</Empty>
          ) : (
            <ConditionTable tagOf={tagByCode} readingOf={readingOf} rows={[
              {
                label: '시작', tagCode: cycle.startTagCode,
                condition: cycle.startValueChangeDetective ? '값 변경 시' : `= ${cycle.startTagValue ?? ''}`,
              },
              ...(cycle.cycleType === 'StartEndChangeDetective' ? [{
                label: '종료', tagCode: cycle.endTagCode,
                condition: cycle.endValueChangeDetective ? '값 변경 시' : `= ${cycle.endTagValue ?? ''}`,
              }] : []),
            ]}>
              <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 px-4 py-3 text-sm border-t border-gray-50">
                <Field label="MES">{cycle.mesRegisterable ? '등록' : '-'}</Field>
              </dl>
            </ConditionTable>
          )}
        </Section>
      </div>

      {ITEM_SECTIONS.map(({ key, title, kind }) => (
        <ItemSection key={key} title={title} kind={kind} items={machine[key]}
          tagOf={tagById} readingOf={readingOf} lockReason={lockReason} onOpen={mode => setOpened({ key, mode })} />
      ))}

      {conditionDialog === 'operation' && (
        <OperationDialog serverId={serverId} machine={machine} devices={devices}
          onClose={() => setConditionDialog(null)}
          onDone={result => { setConditionDialog(null); onConfigSaved(result) }} />
      )}
      {conditionDialog === 'cycle' && (
        <CycleDialog serverId={serverId} machine={machine} devices={devices}
          onClose={() => setConditionDialog(null)}
          onDone={result => { setConditionDialog(null); onConfigSaved(result) }} />
      )}

      {openedSection && opened && (
        <ItemListModal
          title={openedSection.title}
          kind={openedSection.kind}
          items={machine[openedSection.key]}
          tagOf={tagById}
          readingOf={readingOf}
          serverId={serverId}
          machineCode={machine.code}
          devices={devices}
          specTagOf={specTagOf}
          lists={{ alarm: machine.alarms, mete: machine.metes, param: machine.params, signal: machine.signals }}
          initialMode={opened.mode}
          lockReason={lockReason}
          onClose={() => setOpened(null)}
          onSaved={onConfigSaved}
        />
      )}
    </div>
  )
}

interface ConditionRow {
  label: string
  tagCode: string | null
  condition?: string
}

// 가동·사이클 — 조건 한 줄에 태그 하나
function ConditionTable({ rows, tagOf, readingOf, children }: {
  rows: ConditionRow[]
  tagOf: (code: string | null) => TagResolved
  readingOf: (tag: TagResolved) => TagReading | null
  children?: React.ReactNode
}) {
  const hasCondition = rows.some(r => r.condition)
  return (
    <>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs">
          <tr>
            <th className="text-left px-4 py-2 w-16"></th>
            <th className="text-left px-4 py-2">태그</th>
            {hasCondition && <th className="text-left px-4 py-2">조건</th>}
            <th className="text-left px-4 py-2">현재 값</th>
            <th className="text-left px-4 py-2">수집 시각</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map(r => {
            const tag = tagOf(r.tagCode)
            const reading = readingOf(tag)
            return (
              <tr key={r.label}>
                <td className="px-4 py-2.5 text-xs text-gray-500">{r.label}</td>
                <td className="px-4 py-2.5"><TagRef code={r.tagCode} tag={tag} /></td>
                {hasCondition && <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{r.condition ?? '-'}</td>}
                <ReadingValueCell reading={reading} />
                <ReadingTimeCell reading={reading} />
              </tr>
            )
          })}
        </tbody>
      </table>
      {children}
    </>
  )
}

// Operation·Cycle 설정 버튼 — 모니터링 중·오프라인이면 막는다
function SetButton({ lockReason, onClick }: { lockReason: string | null; onClick: () => void }) {
  return (
    <button onClick={onClick} disabled={!!lockReason} title={lockReason ?? undefined}
      className="ml-3 text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-300 disabled:cursor-not-allowed">
      설정
    </button>
  )
}
